import {
  ExIcon,
  ExLeftMenubar,
  ExLeftmenubarAdjustable,
  ExLeftmenubarDivider,
  ExLeftmenubarLink,
} from '@boomi/exosphere';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useBoomiAgentFoundationContext } from '@context/BoomiAgentFoundationContext';
import './LeftMenuManager.scss';
import { useBoomiContext } from '@context/BoomiContext';
import SaveConfirmationDialog from '@components/AgentBuilderWizard/SaveConfirmationDialog';
import { hasPrivilege } from '@utils/data/rbacService';
import UserPrivilege from '@utils/data/userPrivileges';
import SaveConfirmationHelper from '@utils/agent-foundation/SaveConfirmationHelper';
import createNavigationUtils from '@utils/agent-foundation/navigationUtils';
import Features from '@utils/features';

type LeftMenuManagerProps = Partial<{
  isOverlay: boolean;
  isFullHeight: boolean;
}>;

export function handleLeftMenuLinkClick(
  performAction: () => void,
  setIsSaveConfirmationDialogOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setDiscardConfirmationCallback: React.Dispatch<React.SetStateAction<() => void>>,
) {
  if (!SaveConfirmationHelper.callMethod('isDataSaved')) {
    setIsSaveConfirmationDialogOpen(true);
    setDiscardConfirmationCallback(() => performAction);
  } else {
    performAction();
  }
}

function createKeyboardHandler(
  onClick: () => void,
) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };
}

function LeftMenuManager({
  isOverlay,
  isFullHeight = false,
}: LeftMenuManagerProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [isMenuExpanded, setMenuExpanded] = useState(false);
  const [isSaveConfirmationDialogOpen, setIsSaveConfirmationDialogOpen] = useState(false);
  const [discardConfirmationCallback, setDiscardConfirmationCallback] = useState<() => void>(
    () => {});
  const [t] = useTranslation('common');
  const {
    setBuilderDialogTitle,
    setShowBuilderDialog,
    setShowPreview,
    setIsLeftNavCollapsed,
    setTestMessages,
    updateSelectedAgent,
    setIsConversationInProgress,
    setCustomAgentChatMessages,
    setShowCustomAgentConversationPage,
    setIsReffererTool,
    userPrivileges,
    abortRequests,
    setCreateAgentMethod,
    setCurrentView,
  } = useBoomiAgentFoundationContext();
  const hasAgentViewPermission = hasPrivilege(userPrivileges, UserPrivilege.AGENT_VIEW);
  const hasAgentCreatePermission = hasPrivilege(userPrivileges, UserPrivilege.AGENT_CREATE);
  const hasAgentEditPermission = hasPrivilege(userPrivileges, UserPrivilege.AGENT_EDIT);
  const hasAgentInteractPermission = hasPrivilege(userPrivileges, UserPrivilege.AGENT_INTERACT);
  const {
    setConversation,
    setConversationId,
    setShowConversationPage,
  } = useBoomiContext();

  const navigate = useNavigate();

  const navigationUtils = createNavigationUtils({
    setIsSaveConfirmationDialogOpen,
    setShowBuilderDialog,
    setTestMessages,
    clearBoomiAIConversation: () => {
      setConversationId('');
      setConversation([]);
      setShowConversationPage(false);
    },
    clearCustomAgentConversation: () => {
      updateSelectedAgent({ name: 'Boomi GPT', id: '', isFavorite: false });
      setIsConversationInProgress(false);
      setCustomAgentChatMessages([]);
      setShowCustomAgentConversationPage(false);
    },
    abortRequests,
    setConversationId,
    setConversation,
    setShowPreview,
    navigate,
    setIsReffererTool,
    setBuilderDialogTitle,
    t,
  });

  useEffect(() => {
    setBuilderDialogTitle(t('agent_builder_dialog_title_agent_designer'));
  }, []);

  useEffect(() => {
    const updateMenuState = () => {
      if (menuRef.current) {
        const width = menuRef.current.offsetWidth;
        const isExpanded = width > 100; // Adjust threshold as needed
        if (isExpanded !== isMenuExpanded) {
          setMenuExpanded(isExpanded);
          setIsLeftNavCollapsed(!isExpanded);
        }
      }
    };

    // Initial check
    updateMenuState();

    // Add a resize observer to handle dynamic width changes
    const observer = new ResizeObserver(updateMenuState);
    if (menuRef.current) {
      observer.observe(menuRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [isMenuExpanded]);

  useEffect(() => () => {
    setIsLeftNavCollapsed(true);
  }, [setIsLeftNavCollapsed]);

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isActive = (path: string) => location.pathname === path;

  const isEditMode = searchParams.get('mode') === 'edit';

  return (
    <>
      {isSaveConfirmationDialogOpen && (
        <SaveConfirmationDialog
          handleExit={async () => {
            setIsSaveConfirmationDialogOpen(false);
            document.body.style.overflow = 'unset';
            await SaveConfirmationHelper.callMethod('saveData');
            discardConfirmationCallback();
          }}
          handleCancel={async () => {
            setIsSaveConfirmationDialogOpen(false);
            document.body.style.overflow = 'unset';
            try {
              await SaveConfirmationHelper.callMethod('discardChanges');
            } catch (e) {
              // Method not registered, ignore error
            }
            discardConfirmationCallback();
          }}
          handleClose={() => {
            setIsSaveConfirmationDialogOpen(false);
            document.body.style.overflow = 'unset';
          }}
          isSaveDisabled={SaveConfirmationHelper.getMethod('isSaveDisabled')}
        />
      )}
      <div
        id="left-menu-adjustable"
        className={`left-nav
          ${isOverlay ? 'menu-overlay ' : ''} ${
          isFullHeight ? 'menu-full-height ' : ''
        }`}
        ref={menuRef}
      >
        <ExLeftMenubar className="left-nav-container">
          <ExLeftmenubarAdjustable
            adjustable
            expandWidth={260}
            collapseWidth={56}
            isCollapse={!isMenuExpanded}
            showAvatar={false}
          >
            {hasAgentViewPermission && (
              <ExLeftmenubarLink
                label="Home"
                data-testid="left-menubar-home-link"
                onClick={() => {
                  if (isActive('/agentFoundation')) return;
                  handleLeftMenuLinkClick(
                    navigationUtils.navigateToAgentGarden,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                }}
                onKeyDown={createKeyboardHandler(() => {
                  if (isActive('/agentFoundation')) return;
                  handleLeftMenuLinkClick(
                    navigationUtils.navigateToAgentGarden,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                })}
                tooltipText="Home"
                selected={isActive('/home') || isActive('/agents-gallery')}
                className="leftmenu-link"
                isCollapsed={!isMenuExpanded}
              >
                <ExIcon slot="icon" hideBrowserTooltip icon="Home" />
              </ExLeftmenubarLink>
            )}
            {hasAgentCreatePermission && hasAgentEditPermission && (
              <ExLeftmenubarLink
                label="Agent Designer"
                data-testid="left-menubar-agent-designer-link"
                onClick={() => {
                  setCreateAgentMethod('');
                  handleLeftMenuLinkClick(
                    navigationUtils.buildAgent,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                }}
                onKeyDown={createKeyboardHandler(() => {
                  setCreateAgentMethod('');
                  handleLeftMenuLinkClick(
                    navigationUtils.buildAgent,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                })}
                tooltipText="Agent Designer"
                selected={!isEditMode && (isActive('/agent-designer')
                  || isActive('/add-tools') || isActive('/add-source') || isActive('/add-auth0-source'))}
                className="leftmenu-link"
                isCollapsed={!isMenuExpanded}
              >
                <ExIcon slot="icon" hideBrowserTooltip icon="Plus" />
              </ExLeftmenubarLink>
            )}

            {(hasAgentViewPermission || (hasAgentCreatePermission && hasAgentEditPermission)) && (
              <>
                <ExLeftmenubarDivider />
                {isMenuExpanded && (
                <div
                  className="left-nav-category-title"
                >
                  {t('manage')}
                </div>
                ) }
              </>
            )}

            {hasAgentViewPermission && (
              <ExLeftmenubarLink
                label="Agents"
                data-testid="left-menubar-agents-link"
                tooltipText="Agents"
                className="leftmenu-link"
                onClick={() => {
                  if (isActive('/agents')) return;
                  setCurrentView('table');
                  handleLeftMenuLinkClick(
                    navigationUtils.goToAgentList,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                }}
                onKeyDown={createKeyboardHandler(() => {
                  if (isActive('/agents')) return;
                  setCurrentView('table');
                  handleLeftMenuLinkClick(
                    navigationUtils.goToAgentList,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                })}
                selected={isActive('/agents') || (isActive('/agent-designer') && isEditMode)}
                isCollapsed={!isMenuExpanded}
              >
                <ExIcon slot="icon" hideBrowserTooltip icon="AIAgent" />
              </ExLeftmenubarLink>
            )}
            {hasAgentCreatePermission && hasAgentEditPermission && (
              <ExLeftmenubarLink
                label="Tools"
                data-testid="left-menubar-tools-link"
                onClick={() => {
                  if (isActive('/tools')) return;
                  setCurrentView('table');
                  handleLeftMenuLinkClick(
                    navigationUtils.manageTools,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                }}
                onKeyDown={createKeyboardHandler(() => {
                  if (isActive('/tools')) return;
                  setCurrentView('table');
                  handleLeftMenuLinkClick(
                    navigationUtils.manageTools,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                })}
                tooltipText="Tools"
                selected={isActive('/tools') || (isActive('/add-tools') && isEditMode)}
                className="leftmenu-link"
                isCollapsed={!isMenuExpanded}
              >
                <ExIcon slot="icon" hideBrowserTooltip icon="Toolbox" />
              </ExLeftmenubarLink>
            )}
            {hasAgentCreatePermission
              && hasAgentEditPermission
              && Features.isSourcesManagementEnabled() && (
                <ExLeftmenubarLink
                  label="Sources"
                  data-testid="left-menubar-sources-link"
                  onClick={() => {
                    if (isActive('/sources')) return;
                    setCurrentView('table');
                    handleLeftMenuLinkClick(
                      navigationUtils.manageSources,
                      setIsSaveConfirmationDialogOpen,
                      setDiscardConfirmationCallback,
                    );
                  }}
                  onKeyDown={createKeyboardHandler(() => {
                    if (isActive('/sources')) return;
                    setCurrentView('table');
                    handleLeftMenuLinkClick(
                      navigationUtils.manageSources,
                      setIsSaveConfirmationDialogOpen,
                      setDiscardConfirmationCallback,
                    );
                  })}
                  tooltipText="Sources"
                  selected={isActive('/sources') || (!!(searchParams.get('source') && isEditMode))}
                  className="leftmenu-link"
                  isCollapsed={!isMenuExpanded}
                >
                  <ExIcon slot="icon" hideBrowserTooltip icon="Database" />
                </ExLeftmenubarLink>
            )}

            {hasAgentViewPermission && (
              <>
                <ExLeftmenubarDivider />
                {isMenuExpanded && <div className="left-nav-category-title">{t('deploy')}</div> }
              </>
            )}

            {hasAgentCreatePermission && hasAgentEditPermission && (
              <ExLeftmenubarLink
                label={t('environments')}
                data-testid="left-menubar-environments-link"
                tooltipText={t('environments')}
                className="leftmenu-link"
                onClick={() => {
                  if (isActive('/environments')) return;
                  setCurrentView('table');
                  handleLeftMenuLinkClick(
                    navigationUtils.goToEnvironments,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                }}
                onKeyDown={createKeyboardHandler(() => {
                  if (isActive('/environments')) return;
                  setCurrentView('table');
                  handleLeftMenuLinkClick(
                    navigationUtils.goToEnvironments,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                })}
                selected={isActive('/environments')}
                isCollapsed={!isMenuExpanded}
              >
                <ExIcon slot="icon" hideBrowserTooltip icon="Language" />
              </ExLeftmenubarLink>
            )}

            {hasAgentViewPermission && (
              <ExLeftmenubarLink
                label={t('packages_and_deployments')}
                data-testid="left-menubar-packages-deployments-link"
                tooltipText={t('packages_and_deployments')}
                className="leftmenu-link"
                onClick={() => {
                  if (isActive('/packages-deployments')) return;
                  setCurrentView('table');
                  handleLeftMenuLinkClick(
                    navigationUtils.goToPackagesDeployments,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                }}
                onKeyDown={createKeyboardHandler(() => {
                  if (isActive('/packages-deployments')) return;
                  setCurrentView('table');
                  handleLeftMenuLinkClick(
                    navigationUtils.goToPackagesDeployments,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                })}
                selected={isActive('/packages-deployments')}
                isCollapsed={!isMenuExpanded}
              >
                <ExIcon slot="icon" hideBrowserTooltip icon="Publish" />
              </ExLeftmenubarLink>
            )}

            {hasAgentInteractPermission && (
              <>
                <ExLeftmenubarDivider />
                {isMenuExpanded && <div className="left-nav-category-title">{t('conversation')}</div> }
              </>
            )}

            {hasAgentInteractPermission && (
              <ExLeftmenubarLink
                label="Chat"
                data-testid="left-menubar-chat-link"
                onClick={() => {
                  if (isActive('/agentConsumerExperience')) return;
                  handleLeftMenuLinkClick(
                    navigationUtils.goToConsumerExperience,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                }}
                onKeyDown={createKeyboardHandler(() => {
                  if (isActive('/agentConsumerExperience')) return;
                  handleLeftMenuLinkClick(
                    navigationUtils.goToConsumerExperience,
                    setIsSaveConfirmationDialogOpen,
                    setDiscardConfirmationCallback,
                  );
                })}
                tooltipText="Chat"
                selected={isActive('/chat')}
                className="leftmenu-link"
                isCollapsed={!isMenuExpanded}
              >
                <ExIcon slot="icon" hideBrowserTooltip icon="Chatcircletext" />
              </ExLeftmenubarLink>
            )}

          </ExLeftmenubarAdjustable>
        </ExLeftMenubar>
      </div>
    </>
  );
}
export default LeftMenuManager;
